import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMobileAlt, 
  faShield, 
  faClock, 
  faCheck, 
  faTimes,
  faRedo
} from '@fortawesome/free-solid-svg-icons';
import smsVerificationService from '../../services/smsVerificationService';
import './SMSVerification.css';

const SMSVerification = ({ 
  phoneNumber, 
  onVerificationSuccess, 
  onVerificationError,
  purpose = 'cash_on_delivery',
  isLoading = false,
  className = ''
}) => {
  const [step, setStep] = useState('phone'); // 'phone' | 'code'
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [verificationId, setVerificationId] = useState(null);
  const [remainingAttempts, setRemainingAttempts] = useState(3);

  // Timer countdown için useEffect
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(timer => timer - 1);
      }, 1000);
    } else if (timer === 0 && !canResend) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer, canResend]);

  // Telefon numarası değiştiğinde reset
  useEffect(() => {
    if (phoneNumber) {
      setStep('phone');
      setVerificationCode('');
      setError('');
      setSuccess('');
      setTimer(0);
      setCanResend(true);
      setVerificationId(null);
      setRemainingAttempts(3);
    }
  }, [phoneNumber]);

  // SMS OTP Auto-fill için input alanını hazırla
  useEffect(() => {
    if (step === 'code') {
      // Input alanını otomatik algılama için hazırla
      const codeInput = document.querySelector('[data-testid="sms-verification-code"]');
      if (codeInput) {
        // Mobil cihazlarda numeric keyboard açılması için
        codeInput.setAttribute('inputmode', 'numeric');
        codeInput.setAttribute('pattern', '[0-9]*');
        codeInput.setAttribute('autocomplete', 'one-time-code');
        
        // Focus ve select
        setTimeout(() => {
          codeInput.focus();
          codeInput.select();
        }, 100);
      }
    }
  }, [step]);

  // SMS doğrulama kodu gönder
  const sendVerificationCode = async () => {
    if (!phoneNumber) {
      setError('Telefon numarası gereklidir');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await smsVerificationService.sendVerificationCode(phoneNumber, purpose);

      if (data.success) {
        setVerificationId(data.verificationId);
        setStep('code');
        setSuccess('Doğrulama kodu gönderildi');
        setTimer(60); // 60 saniye geri sayım
        setCanResend(false);
        
        // 5 dakika sonra otomatik reset
        setTimeout(() => {
          setStep('phone');
          setError('Doğrulama kodu süresi doldu. Yeni kod talep edin.');
        }, 5 * 60 * 1000);
        
      } else {
        setError(data.error || 'SMS gönderilemedi');
        if (data.waitSeconds) {
          setTimer(data.waitSeconds);
          setCanResend(false);
        }
      }
    } catch (error) {
      console.error('SMS gönderim hatası:', error);
      setError('SMS gönderim sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // SMS doğrulama kodunu kontrol et
  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('6 haneli doğrulama kodunu giriniz');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await smsVerificationService.verifyCode(phoneNumber, verificationCode, purpose);

      if (data.success) {
        setSuccess('Telefon numarası doğrulandı');
        setStep('verified');
        
        // Parent komponente başarı bildir
        if (onVerificationSuccess) {
          onVerificationSuccess({
            phoneNumber: phoneNumber,
            verificationCode: verificationCode,
            verificationId: data.verificationId
          });
        }
      } else {
        setError(data.error || 'Doğrulama başarısız');
        setRemainingAttempts(data.remainingAttempts || 0);
        
        if (onVerificationError) {
          onVerificationError(data.error || 'Doğrulama başarısız');
        }
        
        // Maksimum deneme aşıldıysa başa dön
        if (data.remainingAttempts === 0) {
          setTimeout(() => {
            setStep('phone');
            setRemainingAttempts(3);
          }, 3000);
        }
      }
    } catch (error) {
      console.error('SMS doğrulama hatası:', error);
      const errorMessage = 'Doğrulama sırasında hata oluştu';
      setError(errorMessage);
      
      if (onVerificationError) {
        onVerificationError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Yeni kod talep et
  const requestNewCode = () => {
    setStep('phone');
    setVerificationCode('');
    setError('');
    setSuccess('');
    setTimer(0);
    setCanResend(true);
    setRemainingAttempts(3);
  };

  // Kod input'u için sadece rakam kontrolü
  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  // Enter tuşu ile form submit
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (step === 'phone') {
        sendVerificationCode();
      } else if (step === 'code') {
        verifyCode();
      }
    }
  };

  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`sms-verification ${className}`}>
      {/* Başlık */}
      <div className="sms-verification-header mb-3">
        <FontAwesomeIcon icon={faShield} className="text-success me-2" />
        <span className="fw-bold">Telefon Doğrulama</span>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-3">
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert variant="success" className="mb-3">
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          {success}
        </Alert>
      )}

      {/* Step 1: Telefon Numarası Göster ve SMS Gönder */}
      {step === 'phone' && (
        <div className="sms-step">
          <Form.Group className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faMobileAlt} className="me-2" />
              Telefon Numarası
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={phoneNumber}
                disabled
                className="bg-light"
              />
            </InputGroup>
            <Form.Text className="text-muted">
              Bu telefon numarasına 6 haneli doğrulama kodu gönderilecektir.
            </Form.Text>
          </Form.Group>

          <Button
            variant="success"
            onClick={sendVerificationCode}
            disabled={loading || !canResend || isLoading}
            className="w-100"
            onKeyPress={handleKeyPress}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                Gönderiliyor...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faMobileAlt} className="me-2" />
                {canResend ? 'Doğrulama Kodu Gönder' : `Yeniden Gönder (${formatTimer(timer)})`}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Step 2: Doğrulama Kodu Girişi */}
      {step === 'code' && (
        <div className="sms-step">
          <Form.Group className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faShield} className="me-2" />
              Doğrulama Kodu
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="123456"
                value={verificationCode}
                onChange={handleCodeChange}
                maxLength={6}
                className="text-center sms-code-input"
                autoFocus
                onKeyPress={handleKeyPress}
                autoComplete="one-time-code"
                data-testid="sms-verification-code"
              />
            </InputGroup>
            <Form.Text className="text-muted">
              <FontAwesomeIcon icon={faClock} className="me-1" />
              {phoneNumber} numarasına gönderilen 6 haneli kodu giriniz. 
              {remainingAttempts > 0 && ` (${remainingAttempts} deneme hakkınız kaldı)`}
            </Form.Text>
          </Form.Group>

          <div className="d-grid gap-2">
            <Button
              variant="primary"
              onClick={verifyCode}
              disabled={loading || verificationCode.length !== 6 || isLoading}
              onKeyPress={handleKeyPress}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Doğrulanıyor...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  Doğrula
                </>
              )}
            </Button>

            <Button
              variant="outline-secondary"
              onClick={requestNewCode}
              disabled={loading || isLoading}
              size="sm"
            >
              <FontAwesomeIcon icon={faRedo} className="me-2" />
              Yeni Kod İste
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Doğrulama Başarılı */}
      {step === 'verified' && (
        <div className="sms-step">
          <Alert variant="success" className="mb-0">
            <FontAwesomeIcon icon={faCheck} className="me-2" />
            <strong>Telefon numarası başarıyla doğrulandı!</strong>
            <br />
            <small>{phoneNumber}</small>
          </Alert>
        </div>
      )}

      {/* Güvenlik Bilgisi */}
      <div className="sms-security-info mt-3">
        <small className="text-muted">
          <FontAwesomeIcon icon={faShield} className="me-1" />
          Doğrulama kodunuzu kimseyle paylaşmayın. Kod 5 dakika geçerlidir.
        </small>
      </div>
    </div>
  );
};

export default SMSVerification;
